import { Field, Form, Formik, FormikErrors } from 'formik'
import { FC } from 'react'
import { useCreateWebinarForm } from '../lib/use-create-webinar-form'
import { DateField, TextField } from '@/shared/ui/FormFields'
import { Button } from '@/shared/ui/Button/Button'
import { Loader } from '@mantine/core'
import { createWebinarFormSchema } from '../lib/consts'
import { useTranslation } from 'react-i18next'
import LanguageTabs from '@/features/MultilingualTabs/ui/LanguageTabs'
import { generateLanguageTabsConfig } from '@/shared/lib/utils'
import { MultilingualEntityForm } from '@/features/MultilingualTabs/ui/forms/MultilingualEntityForm'
import ImageCropProvider from '@/app/providers/ImageCropProvider'
import PhotoUploder from '@/features/Photos/UploadAndFrontCrop/PhotoUploader'

type CreateWebinarFormProps = {
  onCloseModal: () => void
}

export const CreateWebinarForm: FC<CreateWebinarFormProps> = props => {
  const { onCloseModal } = props
  const { t } = useTranslation(['webinar'])
  const { initialValues, isLoadingCreateWebinar, submitForm } = useCreateWebinarForm({
    onCloseModal,
  })

  const languageTabs = generateLanguageTabsConfig(
    isLoadingCreateWebinar,
    MultilingualEntityForm,
    'webinar',
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      enableReinitialize
      validationSchema={createWebinarFormSchema(t, languageTabs)}
    >
      <Form>
        <LanguageTabs config={languageTabs} />
        <div className='mt-[1rem]'>
          <DateField
            name='release_date'
            label={t('webinar:release_date')}
            className='flex flex-col w-full mb-4'
            disabled={isLoadingCreateWebinar}
          />
          <TextField
            name='video_url'
            label={t('webinar:video_url')}
            className='flex flex-col w-full mb-4'
            required
            disabled={isLoadingCreateWebinar}
          />
          <Field name='thumbnail'>
            {({ form: { values, setFieldValue } }) => (
              <ImageCropProvider>
                <PhotoUploder
                  label={t('webinar:thumbnail')}
                  initialValue={values.thumbnail}
                  name={'thumbnail'}
                  setField={setFieldValue}
                  aspectRetio={4 / 3}
                />
              </ImageCropProvider>
            )}
          </Field>
        </div>
        <div className='flex flex-row w-full'>
          <Button type='submit' size='md' color='base' disabled={isLoadingCreateWebinar}>
            {isLoadingCreateWebinar ? (
              <Loader size='xs' color='white' />
            ) : (
              t('webinar:buttons.add_webinar')
            )}
          </Button>
        </div>
      </Form>
    </Formik>
  )
}
